import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import moment from "moment";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts components
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import LineInputsLoader from "@/Layouts/components/Skeletons/LineInputsLoader";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Custom components
import TimePickerRange from "./TimePickerRange";

// MUI
import { Grid, Stack } from "@mui/material";
import { DATE_FORMAT } from "@/Constants/date";

// Constants
const HOURS_IN_A_DAY = 24 * 60;
const INVALID_TIME_ERROR = "Invalid time range";
const contentAlignment = "space-between";

const getHoursAndMinutes = (date) => {
	const formatted = moment(date).format(DATE_FORMAT.time24h);
	if (String(formatted).includes(":")) {
		const split = String(formatted).split(":");
		if (split?.length === 2) {
			return +(split?.[0] || 0) * 60 + +(split?.[1] || 0);
		}
	}
	return 0;
};

const getDifferenceInMinutes = (from, to) => getHoursAndMinutes(to) - getHoursAndMinutes(from);

const validateTimeInputs = (nightShiftPay) => {
	const { from, to } = nightShiftPay;
	const validStates = {
		from: Boolean(from),
		to: Boolean(to),
	};
	if (!from || !to) {
		return validStates;
	}
	const minutesDifference = getDifferenceInMinutes(from, to);
	if (minutesDifference === 0) {
		validStates.to = nightShiftPay.isMultipleDays;
		validStates.from = nightShiftPay.isMultipleDays;
		return validStates;
	}
	const fromMinutes = getHoursAndMinutes(from);
	let toMinutes = getHoursAndMinutes(to);
	if (nightShiftPay.isMultipleDays) {
		toMinutes += 24 * 60;
		if (toMinutes - fromMinutes > 24 * 60) {
			validStates.to = false;
		}
	} else if (toMinutes <= fromMinutes) {
		validStates.to = false;
	}
	return validStates;
};

const resolveErrorMessages = (nightShiftPay, errors = []) => {
	const allErrors = [...errors];
	const from = nightShiftPay?.from;
	const to = nightShiftPay?.to;

	if (!from || !to) {
		return [from, to]
			.map((item, key) => {
				const separator = key === 0 ? "from" : "to";
				if (!item) {
					return `Invalid ${separator} time`;
				}
				return null;
			})
			.filter((item) => item);
	}

	const minutesDifference = getDifferenceInMinutes(from, to);

	if (minutesDifference === 0 && !nightShiftPay.isMultipleDays) {
		allErrors.push(INVALID_TIME_ERROR);
	} else {
		const fromMinutes = getHoursAndMinutes(from);
		let toMinutes = getHoursAndMinutes(to);
		if (nightShiftPay.isMultipleDays) {
			toMinutes += HOURS_IN_A_DAY;
			if (toMinutes - fromMinutes > HOURS_IN_A_DAY) {
				allErrors.push(INVALID_TIME_ERROR);
			}
		} else if (toMinutes <= fromMinutes) {
			allErrors.push(INVALID_TIME_ERROR);
		}
	}

	if (!nightShiftPay?.daysSelected?.length) {
		allErrors.push("Must select at least one day");
	}
	return [...new Set(allErrors)];
};

const resolveSelectedDays = (selectedDays) => {
	return selectedDays?.map?.((item) => {
		return { id: item, label: item };
	});
};

const multiSelectDaysData = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((item) => ({
	id: item,
	label: item,
}));

const orderSelectedDays = (selectedDays) => {
	const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
	return selectedDays.sort((a, b) => days.indexOf(a) - days.indexOf(b));
};

const dropdownStyles = { width: "220px" };

export default function NightShiftPay(props) {
	const {
		sectionId = "nightShiftPay",
		content = {
			from: null,
			to: null,
			commission: {
				input: "Percent",
				value: 0,
			},
			prorateByTimeCard: true,
			isMultipleDays: false,
			daysSelected: [],
			offset: 0,
		},
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Hooks
	const { errors, getErrorsMessages, validate } = useValidations();
	const localErrors = errors?.get("commission");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onNightShiftPayUpdate = (updatedNightShiftPay) => {
		const checkTimeInputs = validateTimeInputs(updatedNightShiftPay);
		const trueIsValid =
			checkTimeInputs?.from &&
			checkTimeInputs?.to &&
			updatedNightShiftPay?.daysSelected?.length &&
			updatedNightShiftPay?.commission?.value;
		trueIsValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if ([trueIsValid, wasUpdated.current].every((item) => Boolean(item))) {
			const momentFrom = moment(updatedNightShiftPay?.from);
			const offset = moment(momentFrom).utcOffset();
			updatedNightShiftPay["offset"] = offset;
			onActionUpdate?.(sectionId, null, updatedNightShiftPay);
		}
	};

	// States
	const [nightShiftPay, setNightShiftPay] = useCallbackState(
		{
			from: null,
			to: null,
			commission: {
				input: "Percent",
				value: 0,
			},
			prorateByTimeCard: true,
			isMultipleDays: false,
			daysSelected: [],
			offset: 0,
		},
		onNightShiftPayUpdate
	);

	const validatedTimeInputs = validateTimeInputs(nightShiftPay);
	const commissionError = localErrors?.some?.((error) => Boolean(error?.commission)) || !nightShiftPay?.commission?.value;
	const fromInputError = !validatedTimeInputs?.from;
	const toInputError = !validatedTimeInputs?.to;
	const selectedDaysError = !nightShiftPay?.daysSelected?.length;

	const updateEffectiveDateTime = (dateTime, key) => {
		if (dateTime) {
			update({ ...nightShiftPay, [key]: dateTime });
		}
	};

	const handleUpdateInput = (area, field, value) => {
		const updated = { ...nightShiftPay };
		if (field === "input") {
			updated[area][field] = value;
			updated[area]["value"] = 0;
		} else {
			updated[area][field] = value ? Number(value) : 0;
		}
		update(updated);
	};

	const handleSwitch = (key, value) => {
		const updated = { ...nightShiftPay };
		updated[key] = value;
		update(updated);
	};

	const handleSelectDays = (selected = []) => {
		const updated = { ...nightShiftPay };
		updated["daysSelected"] = orderSelectedDays(selected?.map?.((item) => item?.label) || []);
		update(updated);
	};

	const update = (updatedNightShiftPay) => {
		setNightShiftPay(updatedNightShiftPay);
		wasUpdated.current = true;
	};

	useMemo(() => {
		if (nightShiftPay) {
			validate([
				{
					definition: {
						useKey: "commission",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
							],
						},
						errorsMap: ["commission"],
						message: "Invalid commission",
						validationType: "SIMPLE",
					},
					section: [nightShiftPay],
				},
			]);
		}
	}, [nightShiftPay, validate]);

	useEffect(() => {
		setNightShiftPay(clone(content));
		return () => {
			setNightShiftPay({
				from: null,
				to: null,
				commission: {
					input: "Percent",
					value: 0,
				},
				prorateByTimeCard: true,
				isMultipleDays: false,
				daysSelected: [],
				offset: 0,
			});
			wasUpdated.current = false;
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, content, setNightShiftPay, clearSectionErrors]);

	const errorMessagesToDisplay = resolveErrorMessages(nightShiftPay, errorsMessages);
	return (
		<Grid container direction="column" mt={2} sx={{ padding: "0 32px" }}>
			{loading ? (
				<Stack>
					<LineInputsLoader title inputsAmount={2} justifyContent={"flex-start"} mt={0} />
				</Stack>
			) : (
				<Stack direction={"column"} spacing={2}>
					<Stack direction={"row"} justifyContent={contentAlignment} alignItems={"center"} width={1}>
						<SmartSelector
							multiple
							multipleSelectAll
							tagsVariant={"text"}
							selectBy={"id"}
							showBy={"label"}
							options={multiSelectDaysData}
							selected={resolveSelectedDays(nightShiftPay?.daysSelected) || []}
							inputProps={{
								placeholder: "Select Day(s)",
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							disabled={readOnly || loading}
							error={selectedDaysError}
							loading={loading}
							sx={dropdownStyles}
							onChangeAction={(values) => handleSelectDays(values)}
						/>
					</Stack>
					<Stack direction={"row"} justifyContent={contentAlignment} alignItems={"center"} width={1}>
						<TimePickerRange
							nightShiftPay={nightShiftPay}
							fromInputError={fromInputError}
							toInputError={toInputError}
							loading={loading}
							readOnly={readOnly}
							onSwitchChange={handleSwitch}
							onUpdateTime={updateEffectiveDateTime}
						/>
					</Stack>

					<Stack direction={"row"} justifyContent={contentAlignment} alignItems={"center"} width={1}>
						<Stack direction={"row"} spacing={2} alignItems={"center"}>
							<Stack width={"95px"}>
								<InputFormExtension
									data={[
										{
											type: "Dollar",
											min: 0,
											max: 999999,
										},
										{
											type: "Dollar per Unit",
											min: -99999,
											max: 99999,
											decimalsEditable: false,
											decimals: 2,
										},
										{
											type: "Percent",
											min: 0,
											max: 100,
											decimalsEditable: true,
											decimals: 2,
										},
									]}
									defaultType={nightShiftPay?.["commission"]?.input || "Percent"}
									value={nightShiftPay?.["commission"]?.value || 0}
									error={commissionError}
									onActionUpdate={(value) => handleUpdateInput("commission", "value", value)}
									onChangeType={(_, input) => handleUpdateInput("commission", "input", input)}
									readOnly={readOnly}
								/>
							</Stack>
							<CustomizedSwitches
								textChecked={"Prorate by Time Card"}
								textUnChecked={"Prorate by Time Card"}
								checked={nightShiftPay.prorateByTimeCard}
								onChange={() => handleSwitch("prorateByTimeCard", !nightShiftPay.prorateByTimeCard)}
								disabled={readOnly}
							/>
						</Stack>
					</Stack>

					{Boolean(errorMessagesToDisplay?.length) && (
						<Stack mt={1}>
							{errorMessagesToDisplay.map((message, key) => (
								<TierAlertError key={`error_night_shift_pay_${message}_${key}`} text={message} />
							))}
						</Stack>
					)}
				</Stack>
			)}
		</Grid>
	);
}
NightShiftPay.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		from: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		to: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		commission: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		prorateByTimeCard: PropTypes.bool,
		isMultipleDays: PropTypes.bool,
		daysSelected: PropTypes.array,
		offset: PropTypes.number,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
